import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Palette, Type, Check, Save, Sun, Moon, ImagePlus, Sliders, Link2, Eye, Layers, Globe, X as XIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const Settings = () => {
  const { 
    theme, currentPreset, currentFont, isDarkMode, isCustomTheme,
    customSettings, customColorKey, customColorPalettes, globalTheme,
    setPreset, setFont, setCustomColor, toggleDarkMode, updateCustomSettings,
    applyGlobalTheme, clearGlobalTheme, themePresets, fontOptions 
  } = useTheme();
  const { user, token } = useAuth();
  const isOwner = user?.role === 'owner';

  const [headerUrlInput, setHeaderUrlInput] = useState(customSettings.headerImage || '');
  const [bodyUrlInput, setBodyUrlInput] = useState(customSettings.bodyImage || '');
  const [heroUrlInput, setHeroUrlInput] = useState(customSettings.heroImage || '');
  const [headerPreview, setHeaderPreview] = useState(customSettings.headerImage || '');
  const [bodyPreview, setBodyPreview] = useState(customSettings.bodyImage || '');
  const [heroPreview, setHeroPreview] = useState(customSettings.heroImage || '');
  const [savingGlobal, setSavingGlobal] = useState(false);

  useEffect(() => {
    setHeaderUrlInput(customSettings.headerImage || '');
    setBodyUrlInput(customSettings.bodyImage || '');
    setHeroUrlInput(customSettings.heroImage || '');
    setHeaderPreview(customSettings.headerImage || '');
    setBodyPreview(customSettings.bodyImage || '');
    setHeroPreview(customSettings.heroImage || '');
  }, [customSettings.headerImage, customSettings.bodyImage, customSettings.heroImage, isCustomTheme]);

  const handleSave = () => {
    toast.success('Theme settings saved!');
  };

  const handleApplyGlobal = async () => {
    setSavingGlobal(true);
    const ok = await applyGlobalTheme(token);
    setSavingGlobal(false);
    if (ok) {
      toast.success('Global theme applied! All users will see this theme.');
    } else {
      toast.error('Failed to apply global theme. Owner access required.');
    }
  };

  const handleClearGlobal = async () => {
    setSavingGlobal(true);
    const ok = await clearGlobalTheme(token);
    setSavingGlobal(false);
    if (ok) {
      toast.success('Global theme cleared. Users will see their own preferences.');
    } else {
      toast.error('Failed to clear global theme.');
    }
  };

  const applyHeaderImage = () => {
    if (headerUrlInput.trim()) {
      updateCustomSettings({ headerImage: headerUrlInput.trim() });
      setHeaderPreview(headerUrlInput.trim());
      toast.success('Header image applied!');
    } else {
      updateCustomSettings({ headerImage: '' });
      setHeaderPreview('');
      toast.success('Header image removed');
    }
  };

  const applyBodyImage = () => {
    if (bodyUrlInput.trim()) {
      updateCustomSettings({ bodyImage: bodyUrlInput.trim() });
      setBodyPreview(bodyUrlInput.trim());
      toast.success('Background image applied!');
    } else {
      updateCustomSettings({ bodyImage: '' });
      setBodyPreview('');
      toast.success('Background image removed');
    }
  };

  const handleHeaderUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setHeaderUrlInput(dataUrl);
      updateCustomSettings({ headerImage: dataUrl });
      setHeaderPreview(dataUrl);
      toast.success('Header image uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const applyHeroImage = () => {
    if (heroUrlInput.trim()) {
      updateCustomSettings({ heroImage: heroUrlInput.trim() });
      setHeroPreview(heroUrlInput.trim());
      toast.success('Hero image applied!');
    } else {
      updateCustomSettings({ heroImage: '' });
      setHeroPreview('');
      toast.success('Hero image removed (using default)');
    }
  };

  const handleHeroUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setHeroUrlInput(dataUrl);
      updateCustomSettings({ heroImage: dataUrl });
      setHeroPreview(dataUrl);
      toast.success('Hero image uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const handleBodyUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setBodyUrlInput(dataUrl);
      updateCustomSettings({ bodyImage: dataUrl });
      setBodyPreview(dataUrl);
      toast.success('Background image uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const GlassCard = ({ children, className = '' }) => (
    <div 
      className={`backdrop-blur-xl rounded-xl p-6 transition-all duration-300 ${className}`}
      style={{ 
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="py-4">
      <div className="max-w-2xl mx-auto px-3 sm:px-6">
        <div className="mb-4 sm:mb-6">
          <h1 
            className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1 flex items-center gap-2"
            style={{ color: theme.primary, textShadow: isDarkMode ? theme.glow : 'none' }}
            data-testid="settings-title"
          >
            <Palette className="w-6 h-6 sm:w-8 sm:h-8" />
            Theme Settings
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Customize your BeatLab52 experience</p>
          {globalTheme && (
            <div className="mt-2 flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}>
              <Globe className="w-3.5 h-3.5" />
              Global theme is active — set by an owner
            </div>
          )}
        </div>

        {globalTheme && !isOwner && (
          <div className="mb-4 flex items-center gap-2 text-sm px-4 py-3 rounded-xl" style={{ backgroundColor: `${theme.primary}10`, border: `1px solid ${theme.primary}30`, color: theme.primary }}>
            <Globe className="w-4 h-4 flex-shrink-0" />
            <span>The site theme is set by the owner. Theme settings are view-only.</span>
          </div>
        )}

        {!isCustomTheme && (
          <GlassCard className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDarkMode ? <Moon className="w-6 h-6" style={{ color: theme.primary }} /> : <Sun className="w-6 h-6" style={{ color: theme.primary }} />}
                <div>
                  <h2 className="text-xl font-bold" style={{ color: theme.primary }}>
                    {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Switch between dark and light themes
                  </p>
                </div>
              </div>
              <Switch 
                checked={isDarkMode} 
                onCheckedChange={toggleDarkMode}
                disabled={globalTheme && !isOwner}
                data-testid="dark-mode-switch"
              />
            </div>
          </GlassCard>
        )}

        <GlassCard className="mb-6" style={globalTheme && !isOwner ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
            <Palette className="w-5 h-5" />
            Color Theme
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(themePresets).filter(([k]) => k !== 'custom_1').map(([key, preset]) => (
              <button
                key={key}
                onClick={() => setPreset(key)}
                className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                  currentPreset === key ? 'border-opacity-100 scale-[1.02]' : 'border-opacity-20 hover:border-opacity-50'
                }`}
                style={{ 
                  borderColor: preset.primary,
                  backgroundColor: currentPreset === key ? `${preset.primary}15` : 'transparent',
                }}
                data-testid={`theme-${key}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div 
                    className="w-8 h-8 rounded-full"
                    style={{ 
                      backgroundColor: preset.primary,
                      boxShadow: isDarkMode ? preset.glow : 'none',
                    }}
                  />
                  {currentPreset === key && (
                    <Check className="w-5 h-5" style={{ color: preset.primary }} />
                  )}
                </div>
                <h3 className="font-bold" style={{ color: preset.primary }}>{preset.name}</h3>
                <div className="flex gap-1 mt-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }} />
                  <div className="w-4 h-4 rounded-full border" style={{ borderColor: preset.accent }} />
                </div>
              </button>
            ))}

            <button
              onClick={() => setPreset('custom_1')}
              className={`p-4 rounded-xl border-2 transition-all duration-300 text-left relative overflow-hidden sm:col-span-2 ${
                currentPreset === 'custom_1' ? 'border-opacity-100 scale-[1.01]' : 'border-opacity-20 hover:border-opacity-50'
              }`}
              style={{ 
                borderColor: theme.primary,
                backgroundColor: currentPreset === 'custom_1' ? `${theme.primary}15` : 'transparent',
              }}
              data-testid="theme-custom_1"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ 
                      background: isCustomTheme ? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` : `linear-gradient(135deg, ${themePresets.custom_1.primary}, ${themePresets.custom_1.secondary})`,
                    }}
                  >
                    <ImagePlus className="w-4 h-4 text-black" />
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}>
                    Custom Images + Colors + Controls
                  </span>
                </div>
                {currentPreset === 'custom_1' && (
                  <Check className="w-5 h-5" style={{ color: theme.primary }} />
                )}
              </div>
              <h3 className="font-bold text-lg" style={{ color: isCustomTheme ? theme.primary : themePresets.custom_1.primary }}>Custom Theme 1</h3>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Choose any color scheme, set header &amp; background images, control overlay transparency
              </p>
            </button>
          </div>
        </GlassCard>

        {isCustomTheme && (
          <>
            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: theme.primary }}>
                <Palette className="w-5 h-5" />
                Color Scheme
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Pick a color palette for your custom theme
              </p>

              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {Object.entries(customColorPalettes).map(([key, palette]) => {
                  const isActive = customColorKey === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setCustomColor(key)}
                      className={`relative p-2 rounded-xl border-2 transition-all duration-200 text-center group ${isActive ? 'scale-105' : 'hover:scale-105'}`}
                      style={{
                        borderColor: isActive ? palette.primary : 'rgba(255,255,255,0.06)',
                        backgroundColor: isActive ? `${palette.primary}15` : 'transparent',
                      }}
                      title={palette.name}
                    >
                      <div className="flex justify-center gap-0.5 mb-1">
                        <div className="w-5 h-5 rounded-full border border-white/10" style={{ backgroundColor: palette.primary, boxShadow: isActive ? `0 0 10px ${palette.primary}60` : 'none' }} />
                        <div className="w-3 h-3 rounded-full border border-white/10 mt-1" style={{ backgroundColor: palette.secondary }} />
                      </div>
                      <span className="text-[10px] font-bold leading-tight block truncate" style={{ color: isActive ? palette.primary : 'var(--text-muted)' }}>
                        {palette.name}
                      </span>
                      {isActive && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: palette.primary }}>
                          <Check className="w-2.5 h-2.5 text-black" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <ImagePlus className="w-5 h-5" />
                Header Image
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Add a banner image for the navigation header. Paste an image URL from Pexels or upload your own.
              </p>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      value={headerUrlInput}
                      onChange={(e) => setHeaderUrlInput(e.target.value)}
                      placeholder="https://images.pexels.com/..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/5 border text-sm focus:outline-none focus:ring-2 transition"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', '--tw-ring-color': theme.primary }}
                    />
                  </div>
                  <Button onClick={applyHeaderImage} className="px-4 font-bold" style={{ backgroundColor: theme.primary, color: '#000' }}>
                    Apply
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1" style={{ backgroundColor: 'var(--border-color)' }} />
                  <span className="text-xs font-bold" style={{ color: 'var(--text-muted)' }}>OR</span>
                  <div className="h-px flex-1" style={{ backgroundColor: 'var(--border-color)' }} />
                </div>

                <label className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed cursor-pointer hover:bg-white/5 transition" style={{ borderColor: `${theme.primary}40`, color: theme.primary }}>
                  <ImagePlus className="w-5 h-5" />
                  <span className="text-sm font-bold">Upload Image File</span>
                  <input type="file" accept="image/*" onChange={handleHeaderUpload} className="hidden" />
                </label>

                {headerPreview && (
                  <div className="relative rounded-lg overflow-hidden" style={{ height: '120px' }}>
                    <img src={headerPreview} alt="Header preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,${((customSettings.headerOverlayOpacity ?? 60) / 100 * 0.8).toFixed(2)}), rgba(0,0,0,${Math.min((customSettings.headerOverlayOpacity ?? 60) / 100 + 0.15, 1).toFixed(2)}))` }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-black text-xl tracking-wider" style={{ textShadow: theme.glow }}>HEADER PREVIEW</span>
                    </div>
                    <button
                      onClick={() => { setHeaderUrlInput(''); updateCustomSettings({ headerImage: '' }); setHeaderPreview(''); toast.success('Header image removed'); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-red-500 transition"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: theme.primary }}>
                <Sliders className="w-5 h-5" />
                Header Overlay
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Control the darkness overlay on the header image independently.
              </p>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Eye className="w-4 h-4" style={{ color: theme.primary }} />
                  Header Darkness
                </label>
                <span className="text-lg font-black tabular-nums" style={{ color: theme.primary }}>
                  {customSettings.headerOverlayOpacity ?? 60}%
                </span>
              </div>
              <div className="relative">
                <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-200"
                    style={{
                      width: `${customSettings.headerOverlayOpacity ?? 60}%`,
                      background: `linear-gradient(to right, ${theme.secondary}, ${theme.primary})`,
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="95"
                  step="5"
                  value={customSettings.headerOverlayOpacity ?? 60}
                  onChange={(e) => updateCustomSettings({ headerOverlayOpacity: Number(e.target.value) })}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <div className="flex justify-between mt-1 mb-3">
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Show image</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>More readable</span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[10, 30, 50, 70, 85].map((val) => (
                  <button
                    key={val}
                    onClick={() => updateCustomSettings({ headerOverlayOpacity: val })}
                    className={`py-2 rounded-lg text-xs font-bold transition-all ${(customSettings.headerOverlayOpacity ?? 60) === val ? 'ring-2' : 'hover:bg-white/10'}`}
                    style={{
                      backgroundColor: (customSettings.headerOverlayOpacity ?? 60) === val ? `${theme.primary}20` : 'rgba(255,255,255,0.05)',
                      color: (customSettings.headerOverlayOpacity ?? 60) === val ? theme.primary : 'var(--text-muted)',
                      '--tw-ring-color': theme.primary,
                    }}
                  >
                    {val}%
                  </button>
                ))}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: theme.primary }}>
                <Layers className="w-5 h-5" />
                Header Frame Style
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Choose a frame shape for the header bar.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {[
                  { key: 'straight', label: 'Straight', icon: <div className="w-8 h-4 border-2 rounded-none" style={{ borderColor: theme.primary }} /> },
                  { key: 'rounded', label: 'Rounded', icon: <div className="w-8 h-4 border-2" style={{ borderColor: theme.primary, borderRadius: '0 0 6px 6px' }} /> },
                  { key: 'pill', label: 'Pill', icon: <div className="w-8 h-4 border-2" style={{ borderColor: theme.primary, borderRadius: '0 0 10px 10px' }} /> },
                  { key: 'arch', label: 'Arch', icon: <div className="w-8 h-4 border-2" style={{ borderColor: theme.primary, borderRadius: '0 0 50% 50%' }} /> },
                  { key: 'wave', label: 'Wave', icon: <div className="w-8 h-4 border-2" style={{ borderColor: theme.primary, borderRadius: '0 0 60% 60%' }} /> },
                  { key: 'scallop', label: 'Scallop', icon: <div className="w-8 h-4 border-2" style={{ borderColor: theme.primary, borderRadius: '0 0 100% 100% / 0 0 60% 60%' }} /> },
                  { key: 'diamond', label: 'Diamond', icon: <div className="w-8 h-5" style={{ borderColor: theme.primary, clipPath: 'polygon(0 0, 100% 0, 95% 75%, 50% 100%, 5% 75%)', border: `2px solid ${theme.primary}`, background: `${theme.primary}20` }} /> },
                  { key: 'notch', label: 'Notch', icon: <div className="w-8 h-5" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 75%, 60% 100%, 40% 100%, 0 75%)', border: `2px solid ${theme.primary}`, background: `${theme.primary}20` }} /> },
                  { key: 'slant_right', label: 'Slant R', icon: <div className="w-8 h-5" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0 100%)', border: `2px solid ${theme.primary}`, background: `${theme.primary}20` }} /> },
                  { key: 'slant_left', label: 'Slant L', icon: <div className="w-8 h-5" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 70%)', border: `2px solid ${theme.primary}`, background: `${theme.primary}20` }} /> },
                ].map(({ key, label, icon }) => {
                  const isActive = (customSettings.headerStyle || 'straight') === key;
                  return (
                    <button
                      key={key}
                      onClick={() => updateCustomSettings({ headerStyle: key })}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all duration-200 ${isActive ? 'scale-105' : 'hover:scale-105'}`}
                      style={{
                        borderColor: isActive ? theme.primary : 'rgba(255,255,255,0.06)',
                        backgroundColor: isActive ? `${theme.primary}15` : 'transparent',
                      }}
                      title={label}
                    >
                      {icon}
                      <span className="text-[9px] font-bold" style={{ color: isActive ? theme.primary : 'var(--text-muted)' }}>
                        {label}
                      </span>
                      {isActive && (
                        <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
                          <Check className="w-2 h-2 text-black" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.secondary }}>
                <Layers className="w-5 h-5" />
                Background Image
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Set a full-page background image. Paste an image URL from Pexels or upload your own.
              </p>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      value={bodyUrlInput}
                      onChange={(e) => setBodyUrlInput(e.target.value)}
                      placeholder="https://images.pexels.com/..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/5 border text-sm focus:outline-none focus:ring-2 transition"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', '--tw-ring-color': theme.secondary }}
                    />
                  </div>
                  <Button onClick={applyBodyImage} className="px-4 font-bold" style={{ backgroundColor: theme.secondary, color: '#fff' }}>
                    Apply
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1" style={{ backgroundColor: 'var(--border-color)' }} />
                  <span className="text-xs font-bold" style={{ color: 'var(--text-muted)' }}>OR</span>
                  <div className="h-px flex-1" style={{ backgroundColor: 'var(--border-color)' }} />
                </div>

                <label className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed cursor-pointer hover:bg-white/5 transition" style={{ borderColor: `${theme.secondary}40`, color: theme.secondary }}>
                  <ImagePlus className="w-5 h-5" />
                  <span className="text-sm font-bold">Upload Image File</span>
                  <input type="file" accept="image/*" onChange={handleBodyUpload} className="hidden" />
                </label>

                {bodyPreview && (
                  <div className="relative rounded-lg overflow-hidden" style={{ height: '150px' }}>
                    <img src={bodyPreview} alt="Background preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${(customSettings.overlayOpacity / 100).toFixed(2)})` }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-black text-lg tracking-wider" style={{ textShadow: theme.glow }}>
                        BACKGROUND PREVIEW ({customSettings.overlayOpacity}% overlay)
                      </span>
                    </div>
                    <button
                      onClick={() => { setBodyUrlInput(''); updateCustomSettings({ bodyImage: '' }); setBodyPreview(''); toast.success('Background image removed'); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-red-500 transition"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Sliders className="w-5 h-5" />
                Overlay Controls
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Control the dark overlay on top of your background image.
              </p>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Eye className="w-4 h-4" style={{ color: theme.primary }} />
                      Foreground Darkness
                    </label>
                    <span className="text-lg font-black tabular-nums" style={{ color: theme.primary }}>
                      {customSettings.overlayOpacity}%
                    </span>
                  </div>

                  <div className="relative">
                    <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-200"
                        style={{
                          width: `${customSettings.overlayOpacity}%`,
                          background: `linear-gradient(to right, ${theme.secondary}, ${theme.primary})`,
                        }}
                      />
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="95"
                      step="5"
                      value={customSettings.overlayOpacity}
                      onChange={(e) => updateCustomSettings({ overlayOpacity: Number(e.target.value) })}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>

                  <div className="flex justify-between mt-1">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Show image</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>More readable</span>
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-2">
                  {[10, 30, 50, 70, 85].map((val) => (
                    <button
                      key={val}
                      onClick={() => updateCustomSettings({ overlayOpacity: val })}
                      className={`py-2 rounded-lg text-xs font-bold transition-all ${customSettings.overlayOpacity === val ? 'ring-2' : 'hover:bg-white/10'}`}
                      style={{
                        backgroundColor: customSettings.overlayOpacity === val ? `${theme.primary}20` : 'rgba(255,255,255,0.05)',
                        color: customSettings.overlayOpacity === val ? theme.primary : 'var(--text-muted)',
                        '--tw-ring-color': theme.primary,
                      }}
                    >
                      {val}%
                    </button>
                  ))}
                </div>
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.secondary }}>
                <ImagePlus className="w-5 h-5" />
                Hero Image
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Set a custom image for the homepage hero section (the big area with the title and buttons). Leave empty to use the default.
              </p>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      value={heroUrlInput}
                      onChange={(e) => setHeroUrlInput(e.target.value)}
                      placeholder="https://images.pexels.com/..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/5 border text-sm focus:outline-none focus:ring-2 transition"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', '--tw-ring-color': theme.secondary }}
                    />
                  </div>
                  <Button onClick={applyHeroImage} className="px-4 font-bold" style={{ backgroundColor: theme.secondary, color: '#fff' }}>
                    Apply
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1" style={{ backgroundColor: 'var(--border-color)' }} />
                  <span className="text-xs font-bold" style={{ color: 'var(--text-muted)' }}>OR</span>
                  <div className="h-px flex-1" style={{ backgroundColor: 'var(--border-color)' }} />
                </div>

                <label className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed cursor-pointer hover:bg-white/5 transition" style={{ borderColor: `${theme.secondary}40`, color: theme.secondary }}>
                  <ImagePlus className="w-5 h-5" />
                  <span className="text-sm font-bold">Upload Image File</span>
                  <input type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" />
                </label>

                {heroPreview && (
                  <div className="relative rounded-lg overflow-hidden" style={{ height: '140px' }}>
                    <img src={heroPreview} alt="Hero preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,${((customSettings.heroOverlayOpacity ?? 70) / 100 * 0.9).toFixed(2)}), rgba(0,0,0,${((customSettings.heroOverlayOpacity ?? 70) / 100 * 0.7).toFixed(2)}), rgba(0,0,0,${Math.min((customSettings.heroOverlayOpacity ?? 70) / 100 + 0.1, 1).toFixed(2)}))` }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-white font-black text-2xl tracking-wider block" style={{ textShadow: theme.glow }}>BEATLAB52</span>
                        <span className="text-white/70 text-xs">Hero Preview</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setHeroUrlInput(''); updateCustomSettings({ heroImage: '' }); setHeroPreview(''); toast.success('Hero image removed (using default)'); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-red-500 transition"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: theme.secondary }}>
                <Eye className="w-5 h-5" />
                Hero Section Overlay
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Control the darkness on the homepage hero area (title, subtitle, and buttons).
              </p>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Sliders className="w-4 h-4" style={{ color: theme.secondary }} />
                  Hero Darkness
                </label>
                <span className="text-lg font-black tabular-nums" style={{ color: theme.secondary }}>
                  {customSettings.heroOverlayOpacity ?? 70}%
                </span>
              </div>
              <div className="relative">
                <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-200"
                    style={{
                      width: `${customSettings.heroOverlayOpacity ?? 70}%`,
                      background: `linear-gradient(to right, ${theme.primary}, ${theme.secondary})`,
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="95"
                  step="5"
                  value={customSettings.heroOverlayOpacity ?? 70}
                  onChange={(e) => updateCustomSettings({ heroOverlayOpacity: Number(e.target.value) })}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <div className="flex justify-between mt-1 mb-3">
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Show image</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>More readable</span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[10, 30, 50, 70, 85].map((val) => (
                  <button
                    key={val}
                    onClick={() => updateCustomSettings({ heroOverlayOpacity: val })}
                    className={`py-2 rounded-lg text-xs font-bold transition-all ${(customSettings.heroOverlayOpacity ?? 70) === val ? 'ring-2' : 'hover:bg-white/10'}`}
                    style={{
                      backgroundColor: (customSettings.heroOverlayOpacity ?? 70) === val ? `${theme.secondary}20` : 'rgba(255,255,255,0.05)',
                      color: (customSettings.heroOverlayOpacity ?? 70) === val ? theme.secondary : 'var(--text-muted)',
                      '--tw-ring-color': theme.secondary,
                    }}
                  >
                    {val}%
                  </button>
                ))}
              </div>
            </GlassCard>

            <GlassCard className="mb-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: theme.primary }}>Live Preview</h2>
              <div className="relative rounded-xl overflow-hidden" style={{ height: '220px' }}>
                {bodyPreview ? (
                  <img src={bodyPreview} alt="Preview bg" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)` }} />
                )}
                <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${(customSettings.overlayOpacity / 100).toFixed(2)})` }} />
                <div className="absolute inset-x-0 top-0" style={{
                  ...(headerPreview ? (() => {
                    const previewFrameMap = {
                      straight: {},
                      rounded: { borderRadius: '0 0 12px 12px', overflow: 'hidden' },
                      pill: { borderRadius: '0 0 20px 20px', overflow: 'hidden' },
                      arch: { borderRadius: '0 0 50% 50% / 0 0 14px 14px', overflow: 'hidden' },
                      wave: { borderRadius: '0 0 60% 60% / 0 0 10px 10px', overflow: 'hidden' },
                      diamond: { clipPath: 'polygon(0 0, 100% 0, 98% 85%, 50% 100%, 2% 85%)', overflow: 'hidden' },
                      slant_right: { clipPath: 'polygon(0 0, 100% 0, 100% 80%, 0 100%)', overflow: 'hidden' },
                      slant_left: { clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 80%)', overflow: 'hidden' },
                      notch: { clipPath: 'polygon(0 0, 100% 0, 100% 85%, 55% 100%, 45% 100%, 0 85%)', overflow: 'hidden' },
                      scallop: { borderRadius: '0 0 50px 50px / 0 0 20px 20px', overflow: 'hidden' },
                    };
                    return previewFrameMap[customSettings.headerStyle || 'straight'] || {};
                  })() : {}),
                }}>
                  {headerPreview ? (
                    <div className="relative h-14">
                      <img src={headerPreview} alt="Preview header" className="w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,${((customSettings.headerOverlayOpacity ?? 60) / 100 * 0.8).toFixed(2)}), rgba(0,0,0,${Math.min((customSettings.headerOverlayOpacity ?? 60) / 100 + 0.15, 1).toFixed(2)}))` }} />
                      <div className="absolute inset-0 flex items-center px-4">
                        <span className="text-sm font-black tracking-wider" style={{ color: theme.primary, textShadow: theme.glow }}>BEATLAB52</span>
                        <div className="ml-auto flex gap-3">
                          {['Home', 'Music', 'Radio'].map(l => (
                            <span key={l} className="text-[10px] font-bold text-white/60">{l}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-14 flex items-center px-4" style={{ backgroundColor: 'rgba(10,10,10,0.7)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <span className="text-sm font-black tracking-wider" style={{ color: theme.primary, textShadow: theme.glow }}>BEATLAB52</span>
                      <div className="ml-auto flex gap-3">
                        {['Home', 'Music', 'Radio'].map(l => (
                          <span key={l} className="text-[10px] font-bold text-white/60">{l}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="absolute inset-0 flex items-center justify-center pt-14">
                  <div className="text-center">
                    <div className="w-32 h-4 rounded mb-2 mx-auto" style={{ backgroundColor: `${theme.primary}30` }} />
                    <div className="w-20 h-3 rounded mx-auto" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <div className="flex gap-2 mt-3 justify-center">
                      <div className="w-16 h-6 rounded" style={{ backgroundColor: theme.primary, opacity: 0.8 }} />
                      <div className="w-16 h-6 rounded border" style={{ borderColor: theme.primary, opacity: 0.5 }} />
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>
          </>
        )}

        <GlassCard className="mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
            <Type className="w-5 h-5" />
            Font Style
          </h2>
          
          <div className="space-y-3">
            {fontOptions.map((font) => (
              <button
                key={font.name}
                onClick={() => setFont(font.name)}
                className="w-full p-4 rounded-xl border transition-all duration-300 text-left flex items-center justify-between"
                style={{ 
                  borderColor: currentFont === font.name ? theme.primary : 'var(--border-color)',
                  borderWidth: '1px',
                  fontFamily: font.value,
                  backgroundColor: currentFont === font.name ? `${theme.primary}10` : 'transparent',
                }}
                data-testid={`font-${font.name.toLowerCase().replace(' ', '-')}`}
              >
                <div>
                  <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{font.name}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>The quick brown fox jumps over the lazy dog</p>
                </div>
                {currentFont === font.name && (
                  <Check className="w-5 h-5" style={{ color: theme.primary }} />
                )}
              </button>
            ))}
          </div>
        </GlassCard>

        {!isCustomTheme && (
          <GlassCard className="mb-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: theme.primary }}>Preview</h2>
            
            <div className="space-y-4">
              <div>
                <h3 
                  className="text-2xl font-black uppercase tracking-wider"
                  style={{ 
                    color: theme.primary, 
                    textShadow: isDarkMode ? theme.glow : 'none',
                    fontFamily: fontOptions.find(f => f.name === currentFont)?.value,
                  }}
                >
                  BeatLab52
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontFamily: fontOptions.find(f => f.name === currentFont)?.value }} className="mt-2">
                  This is how your theme will look across the site.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button style={{ backgroundColor: theme.primary, color: '#000' }}>
                  Primary Button
                </Button>
                <Button variant="outline" style={{ borderColor: theme.primary, color: theme.primary }}>
                  Outline Button
                </Button>
              </div>
            </div>
          </GlassCard>
        )}

        {isOwner && (
          <GlassCard className="mb-6">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: theme.primary }}>
              <Globe className="w-5 h-5" />
              Global Theme (Owner Only)
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              Apply your current theme settings globally — all visitors and users will see this theme across the entire site.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={handleApplyGlobal}
                disabled={savingGlobal}
                className="flex-1 font-bold uppercase tracking-wider"
                style={{ backgroundColor: theme.primary, color: '#000' }}
              >
                {savingGlobal ? 'Saving...' : 'Apply Globally'}
              </Button>
              {globalTheme && (
                <Button
                  onClick={handleClearGlobal}
                  disabled={savingGlobal}
                  variant="outline"
                  className="font-bold"
                  style={{ borderColor: 'rgba(239,68,68,0.5)', color: '#EF4444' }}
                >
                  Clear Global
                </Button>
              )}
            </div>
            {globalTheme && (
              <div className="mt-3 text-xs p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', color: 'var(--text-muted)' }}>
                Current global: <span style={{ color: theme.primary }}>{themePresets[globalTheme.preset]?.name || globalTheme.preset}</span>
                {globalTheme.customColor && <> &bull; Color: <span style={{ color: theme.primary }}>{customColorPalettes[globalTheme.customColor]?.name || globalTheme.customColor}</span></>}
                {globalTheme.font && <> &bull; Font: {globalTheme.font}</>}
              </div>
            )}
          </GlassCard>
        )}

        <Button
          onClick={handleSave}
          className="w-full font-bold uppercase tracking-wider"
          style={{ backgroundColor: theme.primary, color: '#000' }}
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default Settings;
